import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Switch,
  Text,
} from '@chakra-ui/react';
import {
  EmailConfigType,
  QuoteEmailTemplate,
  QuotePdfType,
} from '@snap-types/models/enums';
import { SnapSiteConfigEditFormDataUnvalidated } from '@snap-types/models/snap-site-config';
import { UpdateTeamWithConfig } from '@snap-types/models/team';
import { Button } from '@ui/components/Button';
import { ErrorMessage } from '@ui/components/form/ErrorMessage';
import { get, useFormContext } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import { ControlGroupCard } from '../components/ControlGroupCard';
import { PreviewQuoteEmailButton } from './PreviewQuoteEmailButton';
import { TeamEmailConfigSelect } from './TeamEmailConfigSelect';
import { TeamQuoteEmailTemplateSelect } from './TeamQuoteEmailTemplateSelect';
import { TeamQuotePdfSelect } from './TeamQuotePdfSelect';

export function TeamsPanel() {
  const form = useFormContext<SnapSiteConfigEditFormDataUnvalidated>();

  const teams = form.watch('site.teams');

  const handleAddTeam = () => {
    const newTeamUuid = uuid();
    form.setValue('site.teams', [
      ...form.getValues('site.teams'),
      {
        uuid: newTeamUuid,
        teamCode: 'NEW',
        teamName: 'New Team Name',
        currentTeamConfig: {
          active: false,
          // @NOTE(mrs): This UUID is temporary and will NOT be used in the final record
          uuid: uuid(),
          daysUntilExpectedWin: 2,
          daysUntilExpiration: 30,
          quoteEmailSettings: EmailConfigType.DO_NOT_SEND,
          quoteEmailTemplate: QuoteEmailTemplate.DEFAULT,
          quotePdfType: QuotePdfType.DEFAULT,
          quoteIdPrefix: form.getValues('site.code'),
          alphaEmbedTeam: false,
        },
      },
    ]);
  };

  return (
    <Flex direction="column" gap={4} w="full">
      {teams.map((team, index) => (
        <TeamCard key={team.uuid} team={team} index={index} />
      ))}
      <Button onClick={handleAddTeam}>Add Team</Button>
    </Flex>
  );
}

function TeamCard({
  team,
  index,
}: {
  team: UpdateTeamWithConfig;
  index: number;
}) {
  const form = useFormContext<SnapSiteConfigEditFormDataUnvalidated>();
  const teamPrefix = `site.teams.${index}`;

  const isDefaultTeam = team.teamCode === form.getValues('site.code');
  return (
    <ControlGroupCard dataGroupScope={`team-${team.teamCode}`}>
      {isDefaultTeam && (
        <Text fontSize="sm" color="gray.500">
          Note: This is the 'default' team. It cannot be deactivated and must
          have the same code as the Site.
        </Text>
      )}
      <FormControl>
        <Flex flexDirection="row" gap={2}>
          <FormControl width="75px">
            <FormLabel htmlFor={`${team.teamCode}-active`}>Active</FormLabel>
            <Switch
              {...form.register(`site.teams.${index}.currentTeamConfig.active`)}
              id={`${team.teamCode}-active`}
              pt="2"
              isDisabled={isDefaultTeam}
            />
          </FormControl>

          <FormControl width="120px">
            <FormLabel htmlFor={`${team.teamCode}-alphaEmbedTeam`}>
              Alpha Embed
            </FormLabel>
            <Switch
              {...form.register(
                `site.teams.${index}.currentTeamConfig.alphaEmbedTeam`,
              )}
              id={`${team.teamCode}-alphaEmbedTeam`}
              pt="2"
            />
          </FormControl>

          <FormControl width="250px">
            <FormLabel htmlFor={`${team.teamCode}-teamCode_input`}>
              Team Code:
            </FormLabel>
            <Input
              id={`${team.teamCode}-teamCode_input`}
              {...form.register(`site.teams.${index}.teamCode`)}
              minLength={2}
              maxLength={10}
              isDisabled={isDefaultTeam}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor={`${team.teamCode}-teamName_input`}>
              Team Name:
            </FormLabel>
            <Input
              id={`${team.teamCode}-teamName_input`}
              {...form.register(`site.teams.${index}.teamName`)}
              minLength={2}
              maxLength={100}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor={`${team.teamCode}-PDFType_intput`}>
              Quote PDF to Use:
            </FormLabel>
            <TeamQuotePdfSelect team={team} index={index} />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor={`${team.teamCode}-quote_id_prefix`}>
              Quote ID Prefix:
            </FormLabel>
            <Input
              id={`${team.teamCode}-quote_id_prefix`}
              {...form.register(
                `site.teams.${index}.currentTeamConfig.quoteIdPrefix`,
              )}
              maxLength={5} // Visual's QUOTE.ID is nvarchar(15); see quoteIdPrefix in team-config/columns.ts for the full breakdown
            />
          </FormControl>
        </Flex>
      </FormControl>
      <ControlGroupCard
        title="Quote Sending & Timeline"
        dataGroupScope="quote-sending"
      >
        <Flex flexDirection="row" gap={2}>
          <FormControl
            isInvalid={
              !!get(
                form.formState.errors,
                `site.teams.${index}.currentTeamConfig.daysUntilExpiration`,
              )
            }
          >
            <FormLabel htmlFor="days_until_quote_expiration_input">
              Days Until Quote Expiration (default):
            </FormLabel>

            <Input
              id="days_until_quote_expiration_input"
              type="number"
              inputMode="numeric"
              {...form.register(
                `site.teams.${index}.currentTeamConfig.daysUntilExpiration`,
              )}
              min={0}
            />

            <ErrorMessage
              name={`site.teams.${index}.currentTeamConfig.daysUntilExpiration`}
              render={({ message }) => (
                <FormErrorMessage>{message}</FormErrorMessage>
              )}
            />
          </FormControl>

          <FormControl
            isInvalid={
              !!form.formState.errors.site?.teams?.[index]?.currentTeamConfig
                ?.daysUntilExpectedWin
            }
          >
            <FormLabel htmlFor="days_until_expected_win_input">
              Days Until Expected Win (default):
            </FormLabel>

            <Input
              id="days_until_expected_win_input"
              type="number"
              inputMode="numeric"
              {...form.register(
                `site.teams.${index}.currentTeamConfig.daysUntilExpectedWin`,
              )}
              min={0}
            />

            <ErrorMessage
              name={`${teamPrefix}.currentTeamConfig.daysUntilExpectedWin`}
              render={({ message }) => (
                <FormErrorMessage>{message}</FormErrorMessage>
              )}
            />
          </FormControl>
        </Flex>
        <Flex flexDirection="row" gap={2}>
          <FormControl>
            <FormLabel htmlFor="use_email_sending">Use Email Sending</FormLabel>
            <TeamEmailConfigSelect team={team} index={index} />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor={`${team.teamCode}-emailTemplate-select`}>
              Quote Email Template:
            </FormLabel>
            <Flex gap={2}>
              <TeamQuoteEmailTemplateSelect team={team} index={index} />
              <PreviewQuoteEmailButton team={team} index={index} />
            </Flex>
          </FormControl>
        </Flex>
      </ControlGroupCard>
    </ControlGroupCard>
  );
}
